import { useState } from "react";
import { FormHelperText, Stack, styled, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CategorySelectField from "../form-fields/CRMCategorySelectField";
import MultiUploadButton from "../MultiUploadButton";
import { Button } from "@gisce/oficina-virtual-components";
import { data_create_api_resource } from "@/utils/http_functions";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";
import { z } from "zod";
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from "react-hook-form";

const STATUS = {
  FORM: 0,
  SENDING: 1,
  SENT: 2,
};

const NoFlagMuiTelInputStyled = styled(MuiTelInput)`
.MuiTelInput-IconButton {
  display: none;
}
`;

const attachmentSchema = z.object({
  name: z.string(),
  content: z.string(),
});

const UnsignedCRM = () => {
  const { t } = useTranslation();

  const [caseId, setCaseId] = useState<number | null>(null);
  const [status, setStatus] = useState(STATUS.FORM);

  const unsignedCRMSchema = z.object({
    email: z.string()
      .nonempty(t('common:text.required_field'))
      .email({ message: t('common:text.contractation_email_not_valid') }),
    mobile: z.string()
      .nonempty(t('common:text.required_field'))
      .refine(
        (tel) => matchIsValidTel(tel),
        {message: t('common:text.lead_contact_phone_not_valid')}
      ),
    category: z.number({ invalid_type_error: t('common:text.required_field')}),
    subject: z.string().nonempty(t('common:text.required_field')),
    descripcio: z.string().nonempty(t('common:text.required_field')),
    attachments: z
      .array(attachmentSchema)
      .superRefine((attachments, ctx) => {
        attachments.forEach((file, index) => {
          if (
            !/^data:.*\/[a-zA-Z]+;base64,/.test(file.content)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('common:text.invalid_attachment_type'),
              path: [index, 'content'],
            });
          }
        });
      })
  })

  type UnsignedCRMForm = z.infer<typeof unsignedCRMSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UnsignedCRMForm>({
    resolver: zodResolver(unsignedCRMSchema),
    defaultValues: {
      email: "",
      mobile: "",
      category: undefined,
      subject: "",
      descripcio: "",
      attachments: [],
    }
  });

  const onSubmit = async (data: UnsignedCRMForm) => {
    setStatus(STATUS.SENDING);
    try {
      const postData = {
        email: data.email,
        mobile: data.mobile,
        category: data.category,
        subject: data.subject,
        description: data.descripcio,
        attachment: data.attachments,
      };

      const result = await data_create_api_resource(null, "crm/unsigned/", postData)
      setCaseId(result.data.result);
      setStatus(STATUS.SENT);
    } catch (error) {
      console.error("Error submitting form:", error);
      setStatus(STATUS.FORM);
    }
  }

  const renderDone = () => {
    return (
      <div>
        <p>
          { t('common:text.unsigned_contact_success') } <code>#{caseId}</code>
        </p>
      </div>
    );
  }

  const renderForm = () => {
    const isSending = status === STATUS.SENDING;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={2}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t("common:text.unsigned_contact_email") + "*"}
                  fullWidth
                  error={!!errors.email}
                />
                {errors.email && (
                  <FormHelperText error>{errors.email.message}</FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="mobile"
            control={control}
            render={({ field }) => (
              <NoFlagMuiTelInputStyled
                {...field}
                label={t('common:text.unsigned_contact_mobile') + "*"}
                inputProps={{ maxLength: 12 }}
                disableDropdown={true}
                forceCallingCode={true}
                defaultCountry="ES"
                helperText={errors.mobile?.message}
                fullWidth={true}
                error={!!errors.mobile?.message}
              />
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <CategorySelectField
                onChange={(val: number) => field.onChange(val)}
                autolectures={false}
                unsigned={true}
                meta={{
                  error: errors.category?.message,
                  touched: !!errors.category,
                }}
              />
            )}
          />

          <Controller
            name="subject"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t('common:text.unsigned_contact_subject')+ "*"}
                  multiline={true}
                  minRows={5}
                  fullWidth={true}
                  error={!!errors.subject}
                />
                {errors?.subject?.message && (
                  <FormHelperText error={true} id="reclam-error-text">
                    {errors.subject.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="descripcio"
            control={control}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label={t('common:text.unsigned_contact_description')+ "*"}
                  multiline={true}
                  minRows={5}
                  fullWidth={true}
                  error={!!errors.descripcio}
                />
                {errors?.descripcio?.message && (
                  <FormHelperText error={true} id="reclam-error-text">
                    {errors.descripcio.message}
                  </FormHelperText>
                )}
              </>
            )}
          />

          <Controller
            name="attachments"
            control={control}
            render={({ field }) => (
              <MultiUploadButton
                value={field.value}
                label={t('common:text.attachment')}
                anotherLabel={t("common:text.attachment_add")}
                removeLabel={t("common:text.remove")}
                min={1}
                max={6}
                onChange={(files: File[]) => field.onChange(files)}
                accept={"image/png, image/jpeg, image/*,.pdf"}
              />
            )}
          />

          {Array.isArray(errors.attachments) &&
            errors.attachments.map((err, index) =>
              err?.content?.message ? (
                <FormHelperText key={index} error>
                  {t('common:text.attachment_filename')} {index + 1}: {err.content.message}
                </FormHelperText>
              ) : null
            )
          }

          <Button
            color={'primary'}
            variant={'contained'}
            loading={isSending}
            type="submit"
          >
            {t('common:text.unsigned_contact_send')}
          </Button>
        </Stack>
      </form>
    );
  }

  return (
    <>
      <Typography variant="h4" mb={2}>
        {t('common:text.unsigned_contact_title')}
      </Typography>

      {(status === STATUS.FORM || status === STATUS.SENDING) && renderForm()}
      {status === STATUS.SENT && renderDone()}

    </>
  );
};

export default UnsignedCRM;